import { toISODateOnly } from '../../../../utils/dateUtils';
import {
  ExtraFieldInput,
  ExtraFieldValueShortFragment,
  FieldType,
} from '../../__generated__/graphql';

// used internally for internal representation of extra fields and for isolation of graphql contracts
export type ExtraFieldInternalValue = {
  readonly fieldApiName: string;
  readonly internalValue: InternalValue;
  readonly type: FieldType;
};

export type InternalValue = {
  value: string | null;
  // metadata should not be sent to server, but should be stored as string in form values
  metadata?: Record<string, string>;
};

export function mapToExtraFieldInternalValue(
  input: ExtraFieldValueShortFragment,
): ExtraFieldInternalValue {
  return {
    fieldApiName: input.fieldApiName,
    internalValue: mapToInternalValue(input),
    type: input.type,
  };
}

export function mapToExtraFieldInput(
  input: ExtraFieldInternalValue,
): ExtraFieldInput {
  return {
    fieldApiName: input.fieldApiName,
    jsonValue: mapToExtraFieldInputJsonValue(input),
    valueJson: null,
    type: input.type,
  };
}

export const toDisplayValue = ({
  internalValue,
  type,
}: Pick<ExtraFieldInternalValue, 'internalValue' | 'type'>): string | null => {
  if (internalValue === null || internalValue === undefined) {
    return null;
  }
  if (type === FieldType.Reference) {
    return internalValue.metadata?.label ?? '';
  }
  return internalValue.value;
};

export function createInternalLookupValue({
  id,
  label,
}: {
  id: string;
  label: string;
}): string {
  const internalValue: InternalValue = { value: id, metadata: { label } };
  return JSON.stringify(internalValue);
}

export const mapFormValueToInternalValue = (
  value: Date | string | null | boolean | undefined,
  fieldType: FieldType,
): InternalValue => {
  if (fieldType === FieldType.Boolean) {
    return { value: value ? 'true' : 'false' };
  }
  if (value === null || value === '' || value === undefined) {
    return { value: null };
  }
  switch (fieldType) {
    case FieldType.Currency:
    case FieldType.Percent:
    case FieldType.Double:
    case FieldType.Integer:
    case FieldType.Long: {
      return {
        value: typeof value === 'string' ? value : value.toString(),
      };
    }
    case FieldType.Date: {
      return {
        value: toISODateOnly(safeGetDate(value)).toString(),
      };
    }
    case FieldType.Datetime: {
      return {
        value: safeGetDate(value).toISOString(),
      };
    }
    case FieldType.Reference: {
      return typeof value === 'string'
        ? JSON.parse(value)
        : { value: null, metadata: { label: '' } };
    }
    default: {
      return { value: value.toString() };
    }
  }
};

export const mapInternalValueToFormValue = ({
  internalValue,
  type,
}: Pick<ExtraFieldInternalValue, 'internalValue' | 'type'>):
  | string
  | number
  | boolean
  | Date
  | null => {
  if (type === FieldType.Boolean) {
    return internalValue.value === 'true';
  }
  if (internalValue.value === null || internalValue.value === undefined) {
    return '';
  }
  switch (type) {
    case FieldType.Reference:
      return internalValue.value ? JSON.stringify(internalValue) : null;
    default:
      return internalValue.value;
  }
};

// the bug can be cause by yup validation, sometimes it returns string instead of Date
const safeGetDate = (value: unknown): Date => {
  if (typeof value === 'string') {
    return new Date(value);
  } else if (value instanceof Date) {
    return value;
  }
  throw new Error('Invalid type: ' + typeof value);
};

function mapToExtraFieldInputJsonValue({
  internalValue,
  type,
}: Pick<ExtraFieldInternalValue, 'internalValue' | 'type'>): string | null {
  if (internalValue.value === null || internalValue.value === undefined) {
    return null;
  }
  switch (type) {
    case FieldType.Currency:
    case FieldType.Percent:
    case FieldType.Double:
    case FieldType.Integer:
    case FieldType.Long: {
      // it is exactly json of number, we can return it as is
      return internalValue.value;
    }
    case FieldType.Boolean: {
      // it is exactly json of boolean, we can return it as is
      return internalValue.value.toLowerCase();
    }
    default: {
      return JSON.stringify(internalValue.value);
    }
  }
}

function mapToInternalValue(
  input: ExtraFieldValueShortFragment,
): InternalValue {
  let value: string | null = null;
  try {
    value = parseJsonValueToInternalString(input);
  } catch (error) {}
  const metadata = parseJsonValueToMetadata(input);
  return {
    value,
    ...(metadata ? { metadata } : {}),
  };
}

function parseJsonValueToInternalString({
  jsonValue,
  type,
}: Pick<ExtraFieldValueShortFragment, 'jsonValue' | 'type'>): string | null {
  if (jsonValue === null || jsonValue === undefined) {
    return null;
  }
  switch (type) {
    case FieldType.Time: {
      const timeStr: string = JSON.parse(jsonValue);
      // Remove .000Z suffix if present
      return timeStr.indexOf('.') > -1
        ? timeStr.substring(0, timeStr.indexOf('.'))
        : timeStr;
    }
    case FieldType.Currency:
    case FieldType.Percent:
    case FieldType.Double:
    case FieldType.Integer:
    case FieldType.Long: {
      const parsedValue = JSON.parse(jsonValue);
      if (typeof parsedValue === 'number') {
        return parsedValue.toString();
      }
      return null;
    }
    case FieldType.Boolean: {
      const parsedValue = JSON.parse(jsonValue);
      if (typeof parsedValue === 'boolean') {
        return parsedValue.toString();
      }
      return null;
    }
    default:
      return JSON.parse(jsonValue);
  }
}

function parseJsonValueToMetadata({
  jsonValue,
  displayValue,
  type,
}: Pick<ExtraFieldValueShortFragment, 'jsonValue' | 'displayValue' | 'type'>):
  | Record<string, string>
  | undefined {
  if (type === FieldType.Reference && jsonValue) {
    return { label: displayValue ?? '' };
  }
  return undefined;
}
